<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const features = [
  {
    icon: 'ri-store-2-line',
    color: 'primary',
    title: 'Manajemen Supplier',
    desc: 'Simpan data pemasok bahan baku Anda lengkap dengan kontak person dan alamat.',
    to: '/userguide/pembelian/supplier',
  },
  {
    icon: 'ri-file-list-3-line',
    color: 'warning',
    title: 'Purchase Order (PO)',
    desc: 'Buat surat pesanan resmi ke supplier sebelum barang datang. Pantau status pengiriman.',
    to: '/userguide/pembelian/purchase-order',
  },
  {
    icon: 'ri-archive-line',
    color: 'success',
    title: 'Receiving (Penerimaan Barang)',
    desc: 'Catat barang yang datang dari supplier. Stok otomatis bertambah setelah dikonfirmasi.',
    to: '/userguide/pembelian/receiving',
  },
]

const workflow = [
  {
    step: 1,
    icon: 'ri-store-2-line',
    color: 'primary',
    title: 'Daftarkan Supplier',
    desc: 'Input nama, nomor HP, dan alamat supplier Anda.',
    time: '2 menit',
  },
  {
    step: 2,
    icon: 'ri-file-list-3-line',
    color: 'warning',
    title: 'Buat Purchase Order',
    desc: 'Pilih supplier, tentukan bahan yang dipesan dan jumlahnya.',
    time: '5 menit',
  },
  {
    step: 3,
    icon: 'ri-send-plane-line',
    color: 'info',
    title: 'Kirim PO ke Supplier',
    desc: 'Ubah status PO menjadi "Dikirim" agar supplier tahu pesanan Anda.',
    time: '10 detik',
  },
  {
    step: 4,
    icon: 'ri-archive-line',
    color: 'success',
    title: 'Catat Penerimaan Barang',
    desc: 'Saat barang datang, buat Receiving dan isi jumlah yang benar-benar diterima.',
    time: '3 menit',
  },
  {
    step: 5,
    icon: 'ri-check-double-line',
    color: 'success',
    title: 'Konfirmasi → Stok Masuk',
    desc: 'Klik Konfirmasi. Stok bahan baku otomatis bertambah sesuai barang yang diterima.',
    time: '5 detik',
  },
  {
  icon: 'ri-scan-2-line',
  color: 'primary',
  title: 'Scan Struk (AI)',
  desc: 'Foto struk belanja, AI otomatis baca item, qty, dan harga — tanpa input manual.',
  to: '/userguide/pembelian/scan-struk-ai',
},
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <div class="d-flex align-center gap-2 mb-3">
      <RouterLink
        to="/userguide"
        class="text-decoration-none text-medium-emphasis text-body-2"
      >
        User Guide
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <span class="text-body-2 font-weight-medium">Pembelian</span>
    </div>

    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="warning" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-truck-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Modul Pembelian
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Kelola pembelian bahan baku dari supplier hingga stok masuk otomatis
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" class="mb-0">
          <strong>Waktu belajar estimasi:</strong> 15–20 menit untuk memahami alur lengkap.
          Modul ini menghubungkan <strong>supplier → purchase order → penerimaan barang → stok</strong>.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Penjelasan Konsep -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="warning" />
            Mengapa Modul Pembelian Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Bayangkan Anda punya coffee shop. Setiap minggu Anda beli kopi, susu,
              dan gula dari beberapa supplier berbeda. Tanpa pencatatan yang rapi,
              Anda mungkin lupa sudah bayar atau belum, tidak tahu stok mana yang
              sudah datang, atau tidak bisa lacak harga beli per supplier.
            </p>
            <p class="text-body-2 mb-3">
              Dengan Modul Pembelian PintuKasir, semua proses itu tercatat otomatis:
            </p>
            <VRow>
              <VCol cols="12" md="4">
                <div class="d-flex gap-2 mb-2">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="20" class="mt-0.5 flex-shrink-0" />
                  <span class="text-body-2">
                    <strong>Supplier tersimpan</strong> — tidak perlu cari nomor HP supplier di chat WhatsApp
                  </span>
                </div>
              </VCol>
              <VCol cols="12" md="4">
                <div class="d-flex gap-2 mb-2">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="20" class="mt-0.5 flex-shrink-0" />
                  <span class="text-body-2">
                    <strong>PO tercatat</strong> — ada bukti tertulis barang apa yang dipesan
                  </span>
                </div>
              </VCol>
              <VCol cols="12" md="4">
                <div class="d-flex gap-2 mb-2">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="20" class="mt-0.5 flex-shrink-0" />
                  <span class="text-body-2">
                    <strong>Stok otomatis</strong> — setelah barang dikonfirmasi diterima, stok langsung bertambah
                  </span>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Alur Kerja -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔄 Alur Kerja Pembelian (dari Awal hingga Stok Masuk)
        </h5>
        <VCard>
          <VCardText class="pa-4">
            <VRow>
              <VCol
                v-for="(step, i) in workflow"
                :key="step.step"
                cols="12"
                sm="6"
                lg="true"
              >
                <div class="d-flex flex-column align-center text-center">
                  <!-- Step number + icon -->
                  <div class="position-relative mb-3">
                    <VAvatar :color="step.color" variant="tonal" size="56" class="mb-0">
                      <VIcon :icon="step.icon" size="28" />
                    </VAvatar>
                    <VChip
                      size="x-small"
                      color="grey"
                      variant="elevated"
                      class="position-absolute"
                      style="top: -4px; right: -4px; min-width: 20px;"
                    >
                      {{ step.step }}
                    </VChip>
                  </div>

                  <p class="text-body-2 font-weight-bold mb-1">{{ step.title }}</p>
                  <p class="text-caption text-medium-emphasis mb-1">{{ step.desc }}</p>
                  <VChip size="x-small" color="success" variant="tonal">
                    ⏱ {{ step.time }}
                  </VChip>
                </div>

                <!-- Arrow (tidak di item terakhir) -->
                <div
                  v-if="i < workflow.length - 1"
                  class="d-none d-lg-flex justify-center align-center"
                >
                  <!-- Arrow digambarkan via border trick di lg+ screen -->
                </div>
              </VCol>
            </VRow>

            <!-- Garis panah di mobile/tablet: deskripsi teks -->
            <VAlert type="success" variant="tonal" class="mt-4 mb-0 text-body-2">
              <strong>Total waktu setup pertama:</strong> ±10 menit.
              Setelah supplier terdaftar, membuat PO baru cukup 2–3 menit saja.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Feature Cards -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          📖 Panduan Lengkap Per Fitur
        </h5>
      </VCol>
      <VCol
        v-for="feature in features"
        :key="feature.title"
        cols="12"
        md="4"
      >
        <RouterLink :to="feature.to" class="text-decoration-none">
          <VCard variant="outlined" hover class="h-100 feature-card">
            <VCardText class="pa-5">
              <VAvatar :color="feature.color" variant="tonal" size="48" class="mb-3">
                <VIcon :icon="feature.icon" size="26" />
              </VAvatar>
              <h6 class="text-h6 font-weight-bold mb-2">{{ feature.title }}</h6>
              <p class="text-body-2 text-medium-emphasis mb-3">{{ feature.desc }}</p>
              <div class="d-flex align-center gap-1 text-primary text-body-2 font-weight-medium">
                Baca panduan
                <VIcon icon="ri-arrow-right-line" size="16" />
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Urutan Setup -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-information-line" class="me-2" />
            ⚠️ Urutan Setup yang Dianjurkan
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Sebelum bisa membuat Purchase Order, ada beberapa hal yang harus sudah ada di sistem:
            </p>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                <strong>Satuan</strong> sudah dibuat (gram, ml, pcs, dll) →
                <RouterLink to="/userguide/produk-inventory/satuan" class="text-warning">
                  Panduan Satuan
                </RouterLink>
              </li>
              <li class="mb-2">
                <strong>Bahan Baku</strong> sudah didaftarkan →
                <RouterLink to="/userguide/produk-inventory/bahan-baku" class="text-warning">
                  Panduan Bahan Baku
                </RouterLink>
              </li>
              <li class="mb-0">
                <strong>Supplier</strong> sudah ditambahkan → Panduan ada di halaman ini (Supplier)
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Related Links -->
    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Modul Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Bahan Baku', sub: 'Daftarkan bahan sebelum membuat PO', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Pergerakan Stok', sub: 'Lihat riwayat stok masuk dari receiving', icon: 'ri-exchange-line', color: 'secondary', to: '/userguide/produk-inventory/pergerakan-stok' },
              { title: 'Laporan Pembelian', sub: 'Rekap semua transaksi pembelian', icon: 'ri-bar-chart-box-line', color: 'primary', to: '/userguide/laporan/pembelian' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.feature-card {
  transition: all 0.2s ease;
}
.feature-card:hover {
  transform: translateY(-2px);
  border-color: rgba(var(--v-theme-primary), 0.4) !important;
}
</style>
